import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import clsx from 'clsx';
import { Fragment } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BiFlag } from 'react-icons/bi';

import {
  Card,
  Divider,
  InlineSelect,
  LoaderSpinner,
  Modal,
  StatusPill,
  type StatusTone,
} from '@/components';
import { Button, ButtonOutlinedSmall } from '@/components/Button';
import { EmptyPage } from '@/components/EmptyPage';
import { Select, TextArea } from '@/components/Inputs';
import { docStyles as d } from '@/components/ProfileDoc';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  PROVIDER_REPORT_REASONS,
  PROVIDER_REPORT_STATUSES,
  providerReportFormSchema,
  type ProviderReportFormValues,
  type ProviderReportReason,
  type ProviderReportStatus,
} from '@/store/provider-reports/provider-reports.dto';
import {
  PROVIDER_REPORT_REASON_LABELS,
  PROVIDER_REPORT_STATUS_LABELS,
  type SubmittedProviderReport,
} from '@/store/provider-reports/provider-reports.mappers';
import {
  useCreateProviderReportMutation,
  useUpdateProviderReportStatusMutation,
} from '@/store/provider-reports/provider-reports.store';
import { toast } from '@/utils/toast';

import s from './ServiceProviders.module.scss';

const STATUS_TONES: Record<ProviderReportStatus, StatusTone> = {
  new: 'info',
  in_review: 'progress',
  resolved: 'success',
  dismissed: 'neutral',
};

const REASON_OPTIONS = PROVIDER_REPORT_REASONS.map((value) => ({
  value,
  label: PROVIDER_REPORT_REASON_LABELS[value],
}));

const STATUS_OPTIONS = PROVIDER_REPORT_STATUSES.map((value) => ({
  value,
  label: PROVIDER_REPORT_STATUS_LABELS[value],
}));

const statusPill = (status: ProviderReportStatus) => (
  <StatusPill label={PROVIDER_REPORT_STATUS_LABELS[status]} tone={STATUS_TONES[status]} />
);

/**
 * The Reports tab: the reports on this provider as a list, each one's reason
 * and what was said on the left, when and where it stands on the right.
 *
 * A platform admin reads every report and moves its status from the pill; anyone
 * else reads only the reports they filed, and the status is theirs to watch, not
 * to change. `onReport` is absent where the viewer cannot report — their own
 * record — and with it every Report button. `reportLocked` holds the button while
 * the viewer's own report is still open: the service takes one open report per
 * user and provider.
 * record — and with it every Report button.
 */
export const ProviderReportsTab = ({
  reports,
  canChangeStatus,
  onReport,
  reportLocked = false,
}: {
  reports: SubmittedProviderReport[];
  canChangeStatus: boolean;
  onReport?: () => void;
  reportLocked?: boolean;
}) => {
  const isMobile = useIsMobile();
  const {
    mutateAsync: updateStatus,
    isPending,
    variables: pendingChange,
  } = useUpdateProviderReportStatusMutation();

  const changeStatus = async (reportId: string, status: ProviderReportStatus) => {
    try {
      await updateStatus({ reportId, status });
      toast.success('Status updated');
    } catch (err: unknown) {
      console.error(err);
      // 409: reopening would leave the same reporter with two open reports on this provider.
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.error('Couldn’t reopen. Please close their open report first.');
        return;
      }
      toast.error('Couldn’t update the status. Please try again.');
    }
  };

  if (reports.length === 0) {
    return (
      <Card className={d.section}>
        <EmptyPage
          icon={<BiFlag size={40} />}
          title="No reports yet"
          desc="Reports about this provider will show up here."
          action={
            onReport && (
              <Button type="button" onClick={onReport}>
                Report Provider
              </Button>
            )
          }
        />
      </Card>
    );
  }

  return (
    <>
      <Card className={clsx(d.section, onReport && s.reportsWithPinnedAction)}>
        <div className={d.sectionHeader}>
          <h2 className="headline-small">Reports</h2>
          {onReport && !isMobile && (
            <ButtonOutlinedSmall type="button" onClick={onReport} disabled={reportLocked}>
              <BiFlag aria-hidden="true" /> Report
            </ButtonOutlinedSmall>
          )}
        </div>

        {onReport && reportLocked && (
          <p className={clsx('body-small', d.muted)}>
            Your report is still new or in review. You can send another once it’s resolved or
            dismissed.
          </p>
        )}

        <ul className={s.reportList}>
          {reports.map((report, index) => {
            const saving = isPending && pendingChange?.reportId === report.id;
            return (
              <Fragment key={report.id}>
                {index > 0 && <Divider className={s.reportDivider} />}
                <li className={s.report}>
                  <span className={clsx('body-medium', s.reportReason)}>
                    {PROVIDER_REPORT_REASON_LABELS[report.reason]}
                  </span>
                  <span className={clsx('body-small', d.muted, s.reportDate)}>
                    {new Date(report.createdAt).toLocaleDateString()}
                  </span>
                  <p className={clsx('body-small', d.muted, s.reportDetails)}>
                    {report.details || 'No details given.'}
                  </p>
                  <span className={s.reportStatus}>
                    {canChangeStatus ? (
                      <InlineSelect<ProviderReportStatus>
                        label="Status"
                        value={report.status}
                        options={STATUS_OPTIONS}
                        disabled={saving}
                        onChange={(status) => void changeStatus(report.id, status)}
                      >
                        {saving ? (
                          <LoaderSpinner size={16} fullHeight={false} label="" />
                        ) : (
                          statusPill(report.status)
                        )}
                      </InlineSelect>
                    ) : (
                      statusPill(report.status)
                    )}
                  </span>
                </li>
              </Fragment>
            );
          })}
        </ul>
      </Card>

      {/* On a phone the button leaves the header for the bottom edge, where a
          thumb finds it under a list of any length. Outside the card: the card's
          backdrop-filter would make it, not the screen, what `fixed` pins to. */}
      {onReport && isMobile && (
        <div className={s.reportPinned}>
          <Button type="button" onClick={onReport} disabled={reportLocked}>
            <BiFlag aria-hidden="true" /> Report
          </Button>
        </div>
      )}
    </>
  );
};

/**
 * Flagging a provider to the VesselHaven team. The report goes to moderation, not to the
 * provider, who never learns who sent it.
 */
export const ReportProviderModal = ({
  id,
  name,
  onClose,
}: {
  id: string;
  name: string;
  onClose: () => void;
}) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ProviderReportFormValues>({
    resolver: zodResolver(providerReportFormSchema),
    defaultValues: { details: '' },
  });
  const { mutateAsync: report, isPending } = useCreateProviderReportMutation();

  const onSubmit = async (values: ProviderReportFormValues) => {
    try {
      await report({ serviceProviderId: id, values });
      toast.success('Report sent');
      onClose();
    } catch (err: unknown) {
      console.error(err);
      // 409: an open report from this user is already on file.
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.info('You’ve already reported this provider.');
        onClose();
        return;
      }
      toast.error('Couldn’t send the report. Please try again.');
    }
  };

  return (
    <Modal open title={`Report ${name}`} onClose={onClose}>
      <form
        className={clsx(s.ModalContent, 'body-large')}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <p className={d.muted}>
          Our team reviews every report. We never tell the provider who sent it.
        </p>
        <Controller
          control={control}
          name="reason"
          render={({ field }) => (
            <Select<ProviderReportReason | null>
              label="What’s Wrong?"
              mandatory
              placeholder="Choose a reason"
              options={REASON_OPTIONS}
              value={field.value ?? null}
              onChange={(value) => value && field.onChange(value)}
              error={errors.reason?.message}
            />
          )}
        />
        <TextArea
          label="Details"
          placeholder="They invoiced us twice for one haul-out on Jul 18, 2026."
          maxLength={2000}
          error={errors.details?.message}
          {...register('details')}
        />
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Sending…' : 'Send Report'}
        </Button>
      </form>
    </Modal>
  );
};
